import { memo } from 'react';
import {
  Card,
  ContentDrawer,
  SearchInput,
  GroupIcon,
  GroupCommonIcon,
  EmptyContent,
} from '@halo/ui-chat-react';
import {
  Box,
  Center,
  Flex,
  FormatNumber,
  Icon,
  Image,
  Show,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface UserGroupItem {
  avatar: string;
  avatar_color: string;
  gid: string;
  group_name: string;
  member_num: number;
}

function GroupItem(args: UserGroupItem & { onClick?: (gid: string) => void; line?: boolean }) {
  const { t } = useTranslation();
  const { line, group_name: name, avatar: avatar, gid, member_num: memberCount, onClick } = args;

  const handleClick = useCallback(() => {
    onClick?.(gid);
  }, [gid, onClick]);

  return (
    <Box pt="2.5" cursor="pointer" onClick={handleClick} px="4">
      <Flex alignItems="center" gap="2.5">
        <Center w="10" h="10" flex="none">
          {avatar && (
            <Image src={avatar} alt={name} h="full" borderRadius="full" objectFit="cover" />
          )}
        </Center>
        <Flex flex="1" direction="column" gap="1" minW="0" textAlign="left">
          <Flex alignItems="center" minW="0" fontSize="md" gap="2px">
            <Icon>
              <GroupIcon />
            </Icon>
            <Text flex="1" minW="0" fontWeight="semibold" color="fg" lineClamp={1}>
              {name}
            </Text>
          </Flex>
          <Text fontSize="xs" fontWeight="medium" color="fg.subtle" lineClamp={1}>
            <FormatNumber value={memberCount ?? 0} /> {t('chat.groupMembersText')}
          </Text>
        </Flex>
      </Flex>
      <Box mt="2.5" pl="54px" h="1px" bg={line ? 'bg.subtle' : 'transparent'}>
        &nbsp;
      </Box>
    </Box>
  );
}

export const GroupCommonCard = memo(function GroupCommonCard({
  userGroups,
  onClick,
}: {
  userGroups?: UserGroupItem[];
  onClick: (gid: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open);
    },
    [setOpen]
  );

  const count = useMemo(() => userGroups?.length ?? 0, [userGroups]);

  const [keywords, setKeywords] = useState<string | null>(null);

  const _keywords = useDeferredValue(keywords);

  const filteredGroups = useMemo(() => {
    if (!_keywords) return userGroups;
    return userGroups?.filter((userGroup) => userGroup.group_name.includes(_keywords));
  }, [userGroups, _keywords]);

  useEffect(() => {
    setKeywords(null);
  }, [open]);

  return (
    <Card>
      <Flex alignItems="center" gap="2.5" justifyContent="space-between">
        <Flex
          gap="2.5"
          alignItems="center"
          cursor="pointer"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Box fontSize="4xl">
            <GroupCommonIcon />
          </Box>
          <Stack gap="1">
            <Text fontSize="sm" fontWeight="medium">
              {count === 1
                ? t('chat.groupsInCommonOne', { count })
                : t('chat.groupsInCommonOther', { count })}
            </Text>
          </Stack>
        </Flex>
        <ContentDrawer
          open={open}
          onOpenChange={handleOpenChange}
          title={
            count === 1
              ? t('chat.groupsInCommonOne', { count })
              : t('chat.groupsInCommonOther', { count })
          }
        >
          {count > 0 ? (
            <Flex mb="2" flexDirection="column" h="full" gap="2.5">
              <Box overflow="hidden" flex="none">
                <SearchInput
                  value={keywords || ''}
                  onChange={(value) => {
                    setKeywords(value);
                  }}
                  autoFocus={false}
                  inputProps={{ bg: 'bg' }}
                />
              </Box>
              <Box flex="1" minH="0" overflow="auto">
                <Show
                  when={!!filteredGroups?.length}
                  fallback={
                    <Box py="4" textAlign="center" color="fg.subtle">
                      {t('chat.noCommonGroupsYet')}
                    </Box>
                  }
                >
                  <Box bg="bg" borderRadius="lg">
                    {filteredGroups?.map((group, index) => (
                      <GroupItem
                        key={group.gid}
                        onClick={onClick}
                        line={index !== filteredGroups.length - 1}
                        {...group}
                      />
                    ))}
                  </Box>
                </Show>
              </Box>
            </Flex>
          ) : (
            <EmptyContent>{t('chat.noCommonGroupsYet')}</EmptyContent>
          )}
        </ContentDrawer>
      </Flex>
    </Card>
  );
});
